import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  ChevronDown,
  FileText,
} from "lucide-react";
import { Reveal } from "./Reveal";
import Footer from "./Footer";

const C = {
  donker: "#1A2E22",
  groen: "#2D6A4F",
  lichtgroen: "#74C69D",
  geel: "#F4A261",
  creme: "#FAF7F2",
  grijs: "#6B7C6E",
  blauw: "#4a7fa5",
  rood: "#8B3A3A",
};

const tijdlijn = [
  {
    periode: "Jaren '90 – 2016",
    titel: "Langdurig plan voor een dorpsaccommodatie",
    type: "negatief",
    tekst:
      "Al tientallen jaren leeft in Menaam de wens voor een multifunctionele accommodatie. Een werkgroep werkt jarenlang aan plannen, met als laatste concrete uitwerking een Programma van Eisen uit 2016. Het plan is breed gedragen en goed uitgewerkt. Na de fusie naar Waadhoeke in 2018 veranderen de bestuurlijke verhoudingen en in 2022 stopt de werkgroep. Het initiatief strandt, maar de wens blijft leven.",
  },
  {
    periode: "2018",
    titel: "Fusie: Menameradiel gaat op in Waadhoeke",
    type: "kantelpunt",
    tekst:
      "Gemeente Menameradiel wordt samengevoegd met Franekeradeel, Het Bildt en Littenseradiel tot de nieuwe gemeente Waadhoeke. Menaam wordt onderdeel van een grotere bestuurlijke eenheid.",
  },
  {
    periode: "2022",
    titel: "Zwembad Schatzenburg sluit definitief",
    type: "negatief",
    tekst:
      "Al meer dan vijftig jaar fietsen kinderen uit Menaam en omliggende dorpen naar het buitenbad bij Schatzenburg. Als het bad grondige renovatie nodig heeft, komt de eigenaar er niet uit met gemeente Waadhoeke over de financiering. Ruim 1.700 inwoners tekenen een petitie en de regionale media pakken het op. De financiering komt echter niet rond — en het zwembad sluit definitief.",
  },
  {
    periode: "2022",
    titel: "Einde werkgroep dorpsaccommodatie",
    type: "negatief",
    tekst:
      "Na jaren van inspanning stopt de werkgroep die het langdurige plan heeft gedragen. Het initiatief heeft veel kennis en energie gekost. De wens voor een dorpsaccommodatie blijft echter leven in Menaam.",
  },
  {
    periode: "2024 – 2025",
    titel: "Kleinschalig plan voor dorpsvoorziening",
    type: "negatief",
    tekst:
      "Een kleine groep inwoners werkt een plan uit om de bestaande gymzaal een nieuwe bestemming te geven als dorpsruimte. Het idee is praktisch: geen nieuwbouw, maar hergebruik van wat er al is. Door ontwikkelingen binnen het dorp komt het plan uiteindelijk niet van de grond en stopt het initiatief.",
  },
  {
    periode: "Juli 2025",
    titel: "Accommodatiebeleid Waadhoeke vastgesteld",
    type: "positief",
    tekst:
      "Voor het eerst sinds de fusie heeft gemeente Waadhoeke een helder kader voor accommodaties op het gebied van sport, cultuur en welzijn. Het beleid erkent dat er in meerdere dorpen — waaronder Menaam — een opgave ligt voor renovatie of nieuwbouw. Menaam staat op de kaart.",
  },
  {
    periode: "2025",
    titel: "Werkgroep Accommodatie actief",
    type: "positief",
    tekst:
      "Vanuit Dorpsbelang Menaam wordt een nieuwe werkgroep opgericht die proactief meedenkt over de toekomst van de accommodaties in het dorp. Gesprekken met verenigingen, school en gemeente worden gevoerd.",
  },
  {
    periode: "April 2026",
    titel: "Eerste dorpscafé",
    type: "positief",
    tekst:
      "Voor het eerst wordt een dorpscafé georganiseerd waarbij inwoners van Menaam in gesprek gaan over de toekomst van de dorpsvoorzieningen. De opkomst en betrokkenheid laten zien dat de wens voor een gemeenschapsruimte breder leeft dan ooit.",
  },
  {
    periode: "Wordt vervolgd",
    titel: "Het project gaat door",
    type: "kantelpunt",
    tekst:
      "De werkgroep werkt door aan de plannen. Scenario's worden uitgewerkt, gesprekken met de gemeente gevoerd en de bevolking wordt actief betrokken.",
  },
];

const typeMap = {
  positief: { bg: "rgba(45,106,79,.12)", color: C.groen, dot: C.groen, label: "Succes" },
  negatief: { bg: "rgba(139,58,58,.1)", color: C.rood, dot: C.rood, label: "Gestrand" },
  kantelpunt: { bg: "rgba(74,127,165,.12)", color: C.blauw, dot: C.blauw, label: "Kantelpunt" },
};

const TypeBadge = ({ type }) => {
  const s = typeMap[type] || typeMap.kantelpunt;
  return (
    <span
      className="inline-block text-[0.68rem] font-bold tracking-[0.08em] uppercase px-2.5 py-0.5 rounded-full mb-2"
      style={{ background: s.bg, color: s.color }}
    >
      {s.label}
    </span>
  );
};

function TijdlijnItem({ item, index }) {
  const [open, setOpen] = useState(index === 0);
  const s = typeMap[item.type] || typeMap.kantelpunt;
  return (
    <div className="flex gap-5 pb-5">
      <div
        className="relative z-10 shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
        style={{ background: s.dot }}
      >
        {index + 1}
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`historie-item-${index}`}
        className="flex-1 text-left rounded-2xl bg-white border border-[#2D6A4F]/15 p-5 transition-shadow hover:shadow-sm"
      >
        <div className="flex justify-between items-start gap-4">
          <div>
            <TypeBadge type={item.type} />
            <div className="text-xs font-semibold text-[#6B7C6E] tracking-[0.06em] uppercase mb-1">
              {item.periode}
            </div>
            <h3 className="font-heading text-lg font-bold text-[#1A2E22] m-0">{item.titel}</h3>
          </div>
          <ChevronDown
            size={20}
            className={`text-[#6B7C6E] shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div>
        {open && (
          <div className="mt-4 border-t border-[#f0ece6] pt-4">
            <p className="text-sm leading-relaxed text-[#1A2E22]/70 m-0">{item.tekst}</p>
          </div>
        )}
      </button>
    </div>
  );
}

export default function HistoriePage() {
  useEffect(() => {
    document.title = "Historische context | Takomst fan Menaam";
    return () => { document.title = "Takomst fan Menaam | Nieuwe school & accommodaties in Menaam"; };
  }, []);

  const stats = [
    { getal: "~2700", label: "inwoners" },
    { getal: "3×", label: "gestrand initiatief" },
    { getal: "2018", label: "fusie tot Waadhoeke" },
    { getal: "2026", label: "nieuwe kans" },
  ];

  return (
    <div className="bg-[#FAF7F2]">
      {/* Top bar */}
      <div className="sticky top-0 z-50 bg-[#1A2E22]/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[68px] flex items-center justify-between">
          <Link to="/" data-testid="historie-logo" className="flex items-center" aria-label="Takomst fan Menaam">
            <img
              src="/logo-header.png"
              alt="Takomst fan Menaam — mei elkoar, foar elkoar"
              className="h-10 sm:h-11 w-auto object-contain brightness-0 invert"
            />
          </Link>
          <Link
            to="/"
            data-testid="historie-back-link"
            className="inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full bg-[#74C69D] text-[#1A2E22] hover:bg-[#8fd4b0] transition-colors"
          >
            <ArrowLeft size={16} /> Naar de plannen
          </Link>
        </div>
      </div>

      {/* HERO */}
      <section className="relative bg-[#1A2E22] overflow-hidden py-24 sm:py-28">
        <div
          aria-hidden="true"
          className="absolute -right-32 top-0 w-[40rem] h-[40rem] rounded-full bg-[#74C69D]/10 blur-3xl pointer-events-none"
        />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <span className="inline-block text-xs font-bold tracking-[0.18em] uppercase mb-6 px-3.5 py-1.5 rounded-full bg-[#74C69D]/15 text-[#74C69D]">
            Menaam · Historische context
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading text-[#FAF7F2] font-black leading-[1.05] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
          >
            Hoe we hier<br />
            <span className="text-[#74C69D]">gekomen zijn.</span>
          </motion.h1>
          <p className="mt-6 text-lg leading-relaxed text-[#FAF7F2]/75 max-w-2xl">
            Menaam werkt al langer aan betere voorzieningen. Door voort te bouwen op eerdere ervaringen
            en de kansen die zich nu voordoen, kunnen we nu samen nieuwe stappen zetten.
          </p>
          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-white/10 pt-10">
            {stats.map((s) => (
              <div key={s.label}>
                <span className="block font-heading text-4xl font-bold text-[#74C69D]">{s.getal}</span>
                <p className="text-xs uppercase tracking-[0.06em] text-[#FAF7F2]/45 mt-1 m-0">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">
              Constructieve terugblik
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-5">
              Waarom terugkijken?
            </h2>
            <p className="text-[#1A2E22]/70 leading-relaxed mb-4">
              Menaam zet zich al jaren in voor betere voorzieningen in het dorp. In de afgelopen jaren
              zijn verschillende initiatieven gestart. Sommige zijn gerealiseerd, andere niet. Die
              ervaringen vormen een waardevolle basis voor de keuzes die nu worden gemaakt.
            </p>
            <p className="text-[#1A2E22]/70 leading-relaxed">
              Op deze pagina blikken we terug op het verlies van het zwembad en twee eerdere initiatieven
              voor een gemeenschapsruimte. Niet om terug te kijken naar wat er misging, maar om inzicht te
              krijgen in de omstandigheden van toen en de kansen die zich nu voordoen. Zo bouwen we voort
              op wat we hebben geleerd en werken we samen aan een toekomstbestendige voorziening.
            </p>
          </Reveal>
        </div>
      </section>

      {/* TIJDLIJN */}
      <section className="py-20 sm:py-24 bg-[#FAF7F2]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">Tijdlijn</div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-2">
              Wat is er gebeurd?
            </h2>
            <p className="text-sm text-[#6B7C6E] mb-5">Klik op een periode om meer te lezen.</p>
            <div className="flex gap-3 flex-wrap mb-8">
              {["positief", "negatief", "kantelpunt"].map((t) => <TypeBadge key={t} type={t} />)}
            </div>
          </Reveal>
          <div className="relative">
            <div className="absolute top-2 bottom-2 left-4 w-0.5 bg-[#74C69D]/30" />
            {tijdlijn.map((item, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <TijdlijnItem item={item} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* DORPSTROTS */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="text-sm font-bold tracking-[0.14em] uppercase text-[#2D6A4F] mb-3">
              Kracht van het dorp
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#1A2E22] mb-5">
              Menaam heeft ook veel wél voor elkaar gekregen
            </h2>
            <p className="text-[#1A2E22]/70 leading-relaxed mb-4">
              De eerdere plannen die niet doorgingen vertellen maar een deel van het verhaal. Menaam heeft
              door de jaren heen juist laten zien wat een betrokken dorp kan bereiken. Het Feintsje fan
              Menaam is uitgegroeid tot een symbool van de Friese identiteit, het dorp kent een rijk en
              actief verenigingsleven en inwoners zetten zich al jarenlang in voor tal van initiatieven.
              Bij het behoud van het zwembad werden in korte tijd ruim 1.700 handtekeningen verzameld.
            </p>
            <p className="text-[#1A2E22]/70 leading-relaxed">
              Die betrokkenheid vormt ook vandaag de dag een sterke basis. De werkgroep Takomst fan Menaam
              bouwt voort op de kracht van het dorp, de ervaringen uit het verleden en de kansen die zich
              nu voordoen. Samen werken we aan voorzieningen die passen bij de behoeften van nu én van de
              toekomst.
            </p>
            <div className="mt-8 rounded-2xl bg-[#FAF7F2] border-l-4 border-[#74C69D] px-7 py-6">
              <p className="font-heading italic text-lg text-[#1A2E22] leading-relaxed m-0">
                "In feintsje út Menaam, dy jaget troch it griene wâld,<br />
                hy wiuwt tsjin elk jongfaam yn hiel de wide wrâld."
              </p>
              <p className="text-sm text-[#6B7C6E] mt-3 m-0">
                Fedde Schurer, 1943 — het lied dat voor altijd met Menaam verbonden is
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#2D6A4F]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#FAF7F2] mb-2">
              Klaar voor de volgende stap?
            </h2>
            <p className="text-[#FAF7F2]/75 m-0">Lees wat er nu speelt en hoe jij kunt meedoen.</p>
          </div>
          <Link
            to="/"
            data-testid="historie-cta-home"
            className="group inline-flex items-center gap-2 font-bold text-sm px-7 py-3.5 rounded-full bg-[#FAF7F2] text-[#2D6A4F] hover:bg-white transition-colors shrink-0"
          >
            Naar de huidige plannen
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* DISCLAIMER */}
      <section className="py-12 bg-[#FAF7F2] border-t border-[#e8e4de]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="flex gap-3 rounded-2xl bg-white border border-[#e8e4de] px-6 py-5">
            <FileText size={20} className="text-[#6B7C6E] shrink-0 mt-0.5" />
            <p className="text-sm text-[#6B7C6E] leading-relaxed m-0">
              <strong className="text-[#1A2E22]">Disclaimer</strong> — We hebben ons best gedaan om de
              historische feiten op deze pagina zo nauwkeurig mogelijk weer te geven. Het is echter mogelijk
              dat data, namen of details niet in alle gevallen exact kloppen. Heb je een aanvulling of
              correctie? Neem dan contact op via het contactformulier — we passen het graag aan.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
